/**
 * HistoryManager - Undo/Redo system for the Map Editor
 * Uses action-based history for efficient memory usage
 */

export class HistoryManager {
  constructor(maxSize = 100) {
    this.maxSize = maxSize;
    this.history = [];
    this.currentIndex = -1;
    this.listeners = [];
  }

  /**
   * Push an action to the history stack
   * @param {Object} action - { type, data, undo(), redo() }
   */
  push(action) {
    // Remove any redo history
    this.history = this.history.slice(0, this.currentIndex + 1);

    // Add new action
    this.history.push(action);
    this.currentIndex++;

    // Trim if exceeds max size
    if (this.history.length > this.maxSize) {
      this.history.shift();
      this.currentIndex--;
    }

    this.notifyListeners();
  }

  /**
   * Undo the last action
   * @returns {boolean} - Whether undo was successful
   */
  undo() {
    if (!this.canUndo()) return false;

    const action = this.history[this.currentIndex];
    action.undo();
    this.currentIndex--;

    this.notifyListeners();
    return true;
  }

  /**
   * Redo the next action
   * @returns {boolean} - Whether redo was successful
   */
  redo() {
    if (!this.canRedo()) return false;

    this.currentIndex++;
    const action = this.history[this.currentIndex];
    action.redo();

    this.notifyListeners();
    return true;
  }

  canUndo() {
    return this.currentIndex >= 0;
  }

  canRedo() {
    return this.currentIndex < this.history.length - 1;
  }

  /**
   * Clear all history
   */
  clear() {
    this.history = [];
    this.currentIndex = -1;
    this.notifyListeners();
  }

  /**
   * Add a listener for history changes
   * @param {Function} callback
   */
  addListener(callback) {
    this.listeners.push(callback);
  }

  /**
   * Remove a listener
   * @param {Function} callback
   */
  removeListener(callback) {
    this.listeners = this.listeners.filter(l => l !== callback);
  }

  notifyListeners() {
    const state = {
      canUndo: this.canUndo(),
      canRedo: this.canRedo(),
      historyLength: this.history.length,
      currentIndex: this.currentIndex,
    };
    this.listeners.forEach(l => l(state));
  }

  /**
   * Get current history state info
   */
  getState() {
    return {
      canUndo: this.canUndo(),
      canRedo: this.canRedo(),
      historyLength: this.history.length,
      currentIndex: this.currentIndex,
    };
  }
}

/**
 * Action creators for common operations
 */
export const Actions = {
  /**
   * Create a tile placement action
   */
  placeTile(mapData, layer, x, y, oldTileIndex, newTileIndex, applyFn) {
    return {
      type: 'PLACE_TILE',
      data: { layer, x, y, oldTileIndex, newTileIndex },
      undo: () => {
        mapData.layers[layer][y][x] = oldTileIndex;
        applyFn(layer, x, y, oldTileIndex);
      },
      redo: () => {
        mapData.layers[layer][y][x] = newTileIndex;
        applyFn(layer, x, y, newTileIndex);
      },
    };
  },

  /**
   * Create a multi-tile placement action (for fill or rectangle select)
   */
  placeMultipleTiles(mapData, layer, changes, applyFn) {
    // changes: [{ x, y, oldTileIndex, newTileIndex }, ...]
    return {
      type: 'PLACE_MULTIPLE_TILES',
      data: { layer, changes },
      undo: () => {
        changes.forEach(({ x, y, oldTileIndex }) => {
          mapData.layers[layer][y][x] = oldTileIndex;
          applyFn(layer, x, y, oldTileIndex);
        });
      },
      redo: () => {
        changes.forEach(({ x, y, newTileIndex }) => {
          mapData.layers[layer][y][x] = newTileIndex;
          applyFn(layer, x, y, newTileIndex);
        });
      },
    };
  },

  /**
   * Create an entity placement action
   */
  placeEntity(entities, entity, applyFn) {
    return {
      type: 'PLACE_ENTITY',
      data: { entity },
      undo: () => {
        const index = entities.findIndex(e => e.id === entity.id);
        if (index !== -1) {
          entities.splice(index, 1);
        }
        applyFn('remove', entity);
      },
      redo: () => {
        entities.push(entity);
        applyFn('add', entity);
      },
    };
  },

  /**
   * Create an entity removal action
   */
  removeEntity(entities, entity, applyFn) {
    return {
      type: 'REMOVE_ENTITY',
      data: { entity },
      undo: () => {
        entities.push(entity);
        applyFn('add', entity);
      },
      redo: () => {
        const index = entities.findIndex(e => e.id === entity.id);
        if (index !== -1) {
          entities.splice(index, 1);
        }
        applyFn('remove', entity);
      },
    };
  },

  /**
   * Create an entity move action
   */
  moveEntity(entity, oldX, oldY, newX, newY, applyFn) {
    return {
      type: 'MOVE_ENTITY',
      data: { entityId: entity.id, oldX, oldY, newX, newY },
      undo: () => {
        entity.x = oldX;
        entity.y = oldY;
        applyFn(entity, oldX, oldY);
      },
      redo: () => {
        entity.x = newX;
        entity.y = newY;
        applyFn(entity, newX, newY);
      },
    };
  },
};

export default HistoryManager;
